"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Button, Collapse, Select } from "antd"
import { usePathname } from "next/navigation"
import { useFaqLocale } from "../hooks/useFaqLocale"
import { useFaqList } from "../hooks/useFaqList"
import { getLocalizedText } from "../utils/getLocalizedText"
// import { sanitizeFaqHtml } from "../utils/sanitizeFaqHtml"
import { sanitizeCmsHtml } from "@/utils/html/sanitizeCmsHtml"
import { resolveFaqPosition, stripLocalePrefix } from "../utils/resolveFaqPosition"
import { getFaqDisplayMeta } from "../api/matchMeta"
import { getAnswerExcerpt } from "../utils/getAnswerExcerpt"
import FaqCardsRow from "./FaqCardsRow"
import FaqDetailModal, { type FaqDetailModalRef } from "./FaqDetailModal"
import type { FaqDto } from "../types"

type FaqSectionProps = {
  position: string
  defaultLocale: string
  initialFaqs: FaqDto[]
  availableLocales: string[]
  hideOnPathnames?: string[]
}

const INITIAL_VISIBLE_FAQS = 5
const FAQ_LOAD_MORE_COUNT = 10

const FAQ_UI_TEXT: Record<string, { title: string; showMore: string }> = {
  vi: { title: "Câu hỏi thường gặp", showMore: "Xem thêm" },
  en: { title: "Frequently asked questions", showMore: "Show more" },
}

/**
 * FaqSection sống trong layout dùng chung nên không tự re-render khi chuyển
 * trang bằng client-side navigation — dùng usePathname() để tự tính lại
 * position mỗi lần pathname đổi, thay vì tin vào prop `position` tĩnh (chỉ
 * đúng ở lần render đầu). position đổi -> useFaqList tự refetch đúng FAQ.
 * hideOnPathnames cũng được đánh giá lại theo pathname hiện tại vì lý do
 * tương tự (vd trang đã tự nhúng FAQ ở vị trí riêng, slot toàn cục cần ẩn).
 */
export default function FaqSection({
  position: ssrPosition,
  defaultLocale,
  initialFaqs,
  availableLocales: ssrAvailableLocales,
  hideOnPathnames,
}: FaqSectionProps) {
  const pathname = usePathname()
  const position = resolveFaqPosition(pathname)
  const { locale, setLocale } = useFaqLocale(defaultLocale)
  const { faqs, loading, availableLocales } = useFaqList({
    position,
    locale,
    initialFaqs: position === ssrPosition ? initialFaqs : [],
    initialAvailableLocales: position === ssrPosition ? ssrAvailableLocales : [],
  })

  const [hoveredFaqId, setHoveredFaqId] = useState<string | null>(null)
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_FAQS)
  const modalRef = useRef<FaqDetailModalRef>(null)

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_FAQS)
  }, [locale, position])

  const handleOpenDetail = useCallback((faq: FaqDto) => {
    modalRef.current?.open(faq)
  }, [])

  const grouped = useMemo(() => {
    const imageCards: { faq: FaqDto; meta: ReturnType<typeof getFaqDisplayMeta> }[] = []
    const infoCards: { faq: FaqDto; meta: ReturnType<typeof getFaqDisplayMeta> }[] = []
    const list: FaqDto[] = []

    faqs.forEach((faq) => {
      const meta = getFaqDisplayMeta(faq, position, locale)
      if (meta.displayType === "LIST") {
        list.push(faq)
        return
      }

      // Card luôn cần mô tả để UI cố định (đều chiều cao) — thiếu description thì
      // lấy tạm 1 đoạn answer thay thế.
      const cardEntry = { faq, meta: { ...meta, description: meta.description || getAnswerExcerpt(faq, locale) } }
      if (meta.displayType === "IMAGE_CARD") imageCards.push(cardEntry)
      else infoCards.push(cardEntry)
    })

    return { imageCards, infoCards, list }
  }, [faqs, position, locale])

  const normalizedPath = stripLocalePrefix(pathname)
  if (
    hideOnPathnames?.includes(normalizedPath) ||
    (hideOnPathnames?.includes("/dich-vu-visa") && normalizedPath.startsWith("/dich-vu-visa/"))
  ) return null
  if (faqs.length === 0) return null

  const uiText = FAQ_UI_TEXT[locale] || FAQ_UI_TEXT.vi

  return (
    <div className="faq-section">
      <div className="faq-section__header">
        <h2 className="faq-section__title">{uiText.title}</h2>
        {availableLocales.length > 1 && (
          <Select
            size="small"
            value={locale}
            onChange={setLocale}
            className="faq-section__locale"
            options={availableLocales.map((l) => ({ value: l, label: l.toUpperCase() }))}
          />
        )}
      </div>

      <FaqCardsRow
        imageCards={grouped.imageCards}
        infoCards={grouped.infoCards}
        locale={locale}
        onOpen={handleOpenDetail}
      />

      {grouped.list.length > 0 && (
        <>
          <Collapse
            accordion
            bordered
            items={grouped.list.slice(0, visibleCount).map((faq) => ({
              key: faq.id,
              label: (
                <div
                  className="faq-list-item__header"
                  style={{ background: hoveredFaqId === faq.id ? "#F7F8F9" : "transparent" }}
                  onMouseEnter={() => setHoveredFaqId(faq.id)}
                  onMouseLeave={() => setHoveredFaqId((prev) => (prev === faq.id ? null : prev))}
                >
                  {getLocalizedText(faq.question, locale)}
                </div>
              ),
              children: (
                <div
                  className="faq-section__answer prose rich-text"
                  dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(getLocalizedText(faq.answer, locale)) }}
                />
              ),
            }))}
          />
          {visibleCount < grouped.list.length && (
            <div className="faq-section__more">
              <Button
                className="te-btn-round"
                onClick={() => setVisibleCount((count) => count + FAQ_LOAD_MORE_COUNT)}
              >
                {uiText.showMore}
              </Button>
            </div>
          )}
        </>
      )}

      <FaqDetailModal ref={modalRef} locale={locale} />
    </div>
  )
}
